// =============================================================================
// Guestbook tab → real backend (/admin/guestbookEntries).
// Story Change / Photo Update tabs are still mocked. The mock store and the
// real guestbook data are merged for the "All" tab so the UI continues to
// look populated until the other types have backends.
// =============================================================================
import { apiUrl, authHeaders } from './auth';

export type UnmonitoredChangeType =
    | 'guest_book'
    | 'story_change'
    | 'photo_update';

export type UnmonitoredChangeStatus = 'pending' | 'published';

export interface UnmonitoredChange {
    id: number;
    type: UnmonitoredChangeType;
    created_at: string;
    campaign_id: number;
    campaign_person_name: string;
    body: string;
    signature?: string;
    status?: UnmonitoredChangeStatus;
}

export interface UnmonitoredChangeCounts {
    all: number;
    guest_book: number;
    story_change: number;
    photo_update: number;
}

export interface PaginatedUnmonitoredChanges {
    results: UnmonitoredChange[];
    counts: UnmonitoredChangeCounts;
    total: number;
    page: number;
    page_size: number;
}

export interface UnmonitoredChangeFilters {
    type?: UnmonitoredChangeType | 'all';
    page?: number;
    page_size?: number;
}

const LOREM_A =
    'Et sem venenatis aliquam venenatis nibh amet urna. Praesent imperdiet arcu fermentum magna massa scelerisque. Massa aliquet vitae enim mi nec eget sed. Purus adipiscing nulla amet accumsan convallis at id rutrum. Euismod est imperdiet integer mauris integer justo aliquam mi.';
const LOREM_B =
    'Vehicula elementum felis mollis at. Sed aenean accumsan magna euismod id iaculis. Ac mattis sed etiam condimentum. Nisl viverra vulputate urna gravida. Turpis proin facilisis enim vestibulum nam nibh.';
const LOREM_SHORT =
    'Pretium neque sodales tellus sit lacus. Nec lacus pellentesque mi feugiat enim sit vel.';

const MOCK_TYPES: UnmonitoredChangeType[] = ['story_change', 'photo_update'];
const NAMES = [
    'Marvin Hackett',
    'Angel Rosas',
    'Conor Bryan',
    'Marian Turner V',
    'Rik O’Connell',
    'Shannon Mosaìk IV',
    'Janet Funk',
    'Miguel Conn',
];

const seedRows = (): UnmonitoredChange[] => {
    const out: UnmonitoredChange[] = [];
    for (let i = 0; i < 16; i++) {
        const name = NAMES[i % NAMES.length];
        const type = MOCK_TYPES[i % MOCK_TYPES.length];
        const body =
            type === 'photo_update'
                ? LOREM_SHORT
                : i % 2 === 0
                  ? `${LOREM_A}\n\n${LOREM_B}`
                  : LOREM_SHORT;
        out.push({
            id: 1000 + i,
            type,
            created_at: '2026-05-12T13:30:00Z',
            campaign_id: 50000 + i,
            campaign_person_name: name,
            body,
        });
    }
    return out;
};

const mockStore = new Map<number, UnmonitoredChange>();
seedRows().forEach((r) => mockStore.set(r.id, r));

interface GuestbookApiEntry {
    id: number;
    type: 'guest_book';
    created_at: string;
    campaign_id: number;
    campaign_person_name: string;
    message: string;
    signature: string;
    status: UnmonitoredChangeStatus;
}

interface GuestbookListResponse {
    results: GuestbookApiEntry[];
    total: number;
    pending: number;
    page: number;
    page_size: number;
}

const guestbookEntryToRow = (entry: GuestbookApiEntry): UnmonitoredChange => ({
    id: entry.id,
    type: 'guest_book',
    created_at: entry.created_at,
    campaign_id: entry.campaign_id,
    campaign_person_name: entry.campaign_person_name,
    body: entry.message,
    signature: entry.signature,
    status: entry.status,
});

const fetchGuestbookPage = async (
    page: number,
    pageSize: number
): Promise<GuestbookListResponse> => {
    const resp = await fetch(
        `${apiUrl}/admin/guestbookEntries?page=${page}&page_size=${pageSize}`,
        { headers: authHeaders() }
    );
    if (!resp.ok) {
        throw new Error('Failed to load guestbook entries');
    }
    return resp.json();
};

export const fetchUnmonitoredChanges = async (
    filters: UnmonitoredChangeFilters = {}
): Promise<PaginatedUnmonitoredChanges> => {
    const page = filters.page ?? 1;
    const pageSize = filters.page_size ?? 20;
    const type = filters.type ?? 'all';

    const mockRows = Array.from(mockStore.values()).sort((a, b) => b.id - a.id);
    const mockCounts = {
        story_change: mockRows.filter((r) => r.type === 'story_change').length,
        photo_update: mockRows.filter((r) => r.type === 'photo_update').length,
    };

    if (type === 'guest_book' || type === 'all') {
        const guestbook = await fetchGuestbookPage(page, pageSize);
        const guestbookRows = guestbook.results.map(guestbookEntryToRow);

        if (type === 'guest_book') {
            return {
                results: guestbookRows,
                counts: {
                    all:
                        guestbook.total +
                        mockCounts.story_change +
                        mockCounts.photo_update,
                    guest_book: guestbook.total,
                    story_change: mockCounts.story_change,
                    photo_update: mockCounts.photo_update,
                },
                total: guestbook.total,
                page,
                page_size: pageSize,
            };
        }

        const combined = [...guestbookRows, ...mockRows].sort((a, b) => {
            const aTime = new Date(a.created_at).getTime();
            const bTime = new Date(b.created_at).getTime();
            return bTime - aTime;
        });
        const start = (page - 1) * pageSize;
        const results = combined.slice(start, start + pageSize);
        return {
            results,
            counts: {
                all:
                    guestbook.total +
                    mockCounts.story_change +
                    mockCounts.photo_update,
                guest_book: guestbook.total,
                story_change: mockCounts.story_change,
                photo_update: mockCounts.photo_update,
            },
            total:
                guestbook.total +
                mockCounts.story_change +
                mockCounts.photo_update,
            page,
            page_size: pageSize,
        };
    }

    const filtered = mockRows.filter((r) => r.type === type);
    const start = (page - 1) * pageSize;
    const results = filtered.slice(start, start + pageSize);
    return {
        results,
        counts: {
            all:
                mockCounts.story_change +
                mockCounts.photo_update,
            guest_book: 0,
            story_change: mockCounts.story_change,
            photo_update: mockCounts.photo_update,
        },
        total: filtered.length,
        page,
        page_size: pageSize,
    };
};

export const publishGuestbookEntry = async (id: number): Promise<void> => {
    const resp = await fetch(
        `${apiUrl}/admin/guestbookEntries/${id}/publish`,
        { method: 'PATCH', headers: authHeaders() }
    );
    if (!resp.ok) throw new Error('Failed to publish guestbook entry');
};

export const deleteGuestbookEntry = async (id: number): Promise<void> => {
    const resp = await fetch(`${apiUrl}/admin/guestbookEntries/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!resp.ok && resp.status !== 204) {
        throw new Error('Failed to delete guestbook entry');
    }
};

export const approveUnmonitoredChange = async (
    row: UnmonitoredChange
): Promise<void> => {
    if (row.type === 'guest_book') {
        await publishGuestbookEntry(row.id);
        return;
    }
    mockStore.delete(row.id);
};

export const deleteUnmonitoredChange = async (
    row: UnmonitoredChange
): Promise<void> => {
    if (row.type === 'guest_book') {
        await deleteGuestbookEntry(row.id);
        return;
    }
    mockStore.delete(row.id);
};

export const bulkApproveUnmonitoredChanges = async (
    rows: UnmonitoredChange[]
): Promise<void> => {
    await Promise.all(rows.map((row) => approveUnmonitoredChange(row)));
};

export const bulkDeleteUnmonitoredChanges = async (
    rows: UnmonitoredChange[]
): Promise<void> => {
    await Promise.all(rows.map((row) => deleteUnmonitoredChange(row)));
};
